/**
 * Executive Dashboard — configurable widget grid for PAEs and leadership.
 *
 * Widgets render existing govdata section components inside a draggable
 * react-grid-layout grid. Dashboard layout and widget config persist via
 * the /api/exec/ endpoints.
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Responsive, WidthProvider } from 'react-grid-layout'
import { Plus, LayoutDashboard, Share2, Check, Printer } from 'lucide-react'
import 'react-grid-layout/css/styles.css'

import { ExecDashboardProvider, useExecDashboard } from '../contexts/ExecDashboardContext'
import { useAgent } from '../contexts/AgentContext'
import { execAPI } from '../services/exec'
import WidgetCard from '../components/exec/WidgetCard'
import WidgetErrorBoundary from '../components/exec/WidgetErrorBoundary'
import MeasureWidget from '../components/exec/MeasureWidget'
import WidgetPicker from '../components/exec/WidgetPicker'
import FYRangeSlider from '../components/exec/FYRangeSlider'
import GoalsPanel from '../components/exec/GoalsPanel'
import AttentionWidget from '../components/exec/AttentionWidget'
import DataFreshnessPill from '../components/exec/DataFreshnessPill'
import { connectChartGroup } from '../components/exec/generic/echarts-theme'
import type { GridLayoutItem, DashboardWidget, WidgetConfig } from '../types/exec'
import type ReactGridLayoutNS from 'react-grid-layout'

const ResponsiveGridLayout = WidthProvider(Responsive)

/**
 * Default starter widgets for a new dashboard (Phase G5).
 *
 * Each entry includes an explicit `config` so the new dashboard surfaces a
 * curated layout — not just measure-default fallbacks. Layout: 2 cols × 5 rows
 * with the wide vendor-concentration widget spanning both columns mid-page.
 *
 * Notes:
 * - KPI status row + goal status rollup + Attention widget (the v3 plan's full
 *   curated default) come in once their composite presets exist (G3/G4/I1).
 * - Single-bid / Modification / Deobligation widget triad lands once the
 *   underlying configs are added in Phase K.
 */
const DEFAULT_WIDGETS: {
  measure: string
  title?: string
  config?: Record<string, unknown>
  w: number
  h: number
}[] = [
  // Speed to Contract — boxplot via the bespoke section component.
  {
    measure: 'speed_to_contract',
    title: 'Speed to Contract (Distribution)',
    config: { chart_type: 'boxplot' },
    w: 6, h: 5,
  },
  // Competition trend with peer-line overlay when scoped to a portfolio.
  {
    measure: 'competition_health',
    title: 'Competition Rate (Trend)',
    config: {
      group_by: 'fiscal_year',
      metrics: ['full_open_pct', 'not_competed_pct'],
      chart_type: 'line',
    },
    w: 6, h: 5,
  },
  // Treemap — hierarchy / spend rollup. Replaces the bar-chart "Spend by
  // Color of Money" with a magnitudes-at-a-glance treemap that uses 2D area
  // for relative scale and supports drill via leafDepth.
  {
    measure: 'activity_by_vertical',
    title: 'Spend by Vertical (Treemap)',
    config: {
      group_by: 'psc_level1_code',
      metrics: ['total_obligated'],
      chart_type: 'treemap',
      sort_by: '-total_obligated',
    },
    w: 6, h: 5,
  },
  // Sankey — flow viz: which color of money flows into which vehicle. The
  // group_by → series_by pair becomes source → target on the renderer.
  {
    measure: 'office_performance',
    title: 'Spend Flow: Color of Money → Vehicle',
    config: {
      group_by: 'color_of_money',
      series_by: 'vehicle_category',
      metrics: ['total_obligated'],
      chart_type: 'sankey',
    },
    w: 6, h: 5,
  },
  // Scatter — outlier detection on competition health. X = F&O%, Y =
  // single-bid %, bubble size = total awards. MAJCOMs with high F&O AND
  // high single-bid are doing competition theatre.
  {
    measure: 'competition_health',
    title: 'Competition Health Scatter',
    config: {
      group_by: 'macom',
      metrics: ['full_open_pct', 'single_offer_pct'],
      size_metric: 'award_count',
      chart_type: 'scatter',
      limit: 30,
    },
    w: 6, h: 5,
  },
  // Slope — what changed FY-to-FY. Each line is one MAJCOM; left endpoint
  // is oldest FY in scope, right is newest. Rank mode (1 = best) auto-spreads
  // lines so direction-of-movement is read at a glance even when underlying
  // F&O % values cluster near the ceiling.
  {
    measure: 'competition_health',
    title: 'F&O % Rank-Change Between FYs',
    config: {
      group_by: 'macom',
      series_by: 'fiscal_year',
      metrics: ['full_open_pct'],
      chart_type: 'slope',
      as_rank: true,
    },
    w: 6, h: 5,
  },
  // Vendor Concentration (Pareto) — full-width row.
  {
    measure: 'vendor_concentration',
    title: 'Vendor Concentration (Pareto)',
    config: {
      group_by: 'fiscal_year',
      metrics: ['total_obligated'],
      chart_type: 'bar',
      sort_by: '-total_obligated',
      limit: 20,
    },
    w: 12, h: 6,
  },
  // Small Business trend — multi-line so each SB program reads as its own
  // trajectory rather than 4 grouped bars per FY.
  {
    measure: 'small_biz_engagement',
    title: 'Small Business (Trend)',
    config: {
      group_by: 'fiscal_year',
      metrics: ['count_8a', 'count_hubzone', 'count_sdvosb', 'count_wosb'],
      chart_type: 'line',
    },
    w: 6, h: 5,
  },
]

/**
 * Pack a list of (id, w, h) tiles into the 12-col grid, breaking onto a
 * new row when the next tile wouldn't fit. Used both at first-create time
 * and when self-healing a stale layout (see `isLayoutBroken` below).
 */
function packLayout(tiles: { id: string; w: number; h: number }[]): GridLayoutItem[] {
  const out: GridLayoutItem[] = []
  let cursorX = 0
  let cursorY = 0
  let rowHeight = 0
  for (const t of tiles) {
    if (cursorX + t.w > 12) {
      cursorX = 0
      cursorY += rowHeight
      rowHeight = 0
    }
    out.push({ i: t.id, x: cursorX, y: cursorY, w: t.w, h: t.h })
    cursorX += t.w
    rowHeight = Math.max(rowHeight, t.h)
  }
  return out
}

/**
 * Self-heal heuristic for default dashboards whose saved layout was
 * created before the curated layout existed. The most common failure mode
 * is "every tile pinned at x=0" — the result of an older path that wrote
 * the layout without packing. Also catches missing layout items (a widget
 * was added but no entry was written).
 *
 * Conservative: only fires when ≥3 tiles are at x=0, or when the layout
 * has fewer items than widgets. Won't blow away a leader's deliberate
 * customization.
 */
function isLayoutBroken(layout: GridLayoutItem[], widgetCount: number): boolean {
  if (!layout || layout.length === 0) return widgetCount > 0
  if (layout.length < widgetCount) return true
  const allAtZero = layout.every((item) => item.x === 0)
  return allAtZero && layout.length >= 3
}

/**
 * Pick a sensible default w/h for a widget based on its measure. Used by
 * the self-heal path where we can't read DEFAULT_WIDGETS' grid hints
 * because the dashboard was created with a different widget set.
 */
function widgetDefaultSize(widget: DashboardWidget): { w: number; h: number } {
  // Vendor concentration's Pareto reads better full-width.
  if (widget.measure === 'vendor_concentration') return { w: 12, h: 6 }
  return { w: 6, h: 5 }
}

/**
 * Dev-only seed: ensures the local app has a representative portfolio
 * scope + a starter goal set so the dashboard demonstrates peer-line
 * overlays, the Attention widget, and goal-status chips out of the box.
 * Idempotent — only creates state when none exists. Gated on
 * `import.meta.env.DEV` so production / staging builds never auto-seed.
 *
 * Returns the portfolio_group id to scope a fresh dashboard to, or
 * undefined if seeding is skipped.
 */
async function seedDemoStateIfDev(): Promise<string | undefined> {
  if (!import.meta.env.DEV) return undefined

  // Use existing state when it's already there — no clobbering.
  const [existingPortfolios, existingGoals] = await Promise.all([
    execAPI.getPortfolioGroups(),
    execAPI.getGoals(),
  ])

  let portfolioId: string | undefined
  if (existingPortfolios.length === 0) {
    // "All Air Force" picks up the most demo-ready slice — sub_agency
    // 5700 has rich performance_fact + speed-to-contract + vendor data
    // so every default-dashboard widget renders meaningfully.
    const created = await execAPI.createPortfolioGroup({
      name: 'All Air Force',
      description: 'Demo seed — Department of the Air Force.',
    })
    await execAPI.addPortfolioGroupRule(created.id, {
      hierarchy_level: 'sub_agency',
      entity_code: '5700',
      entity_name: 'Department of the Air Force',
    })
    portfolioId = created.id
  } else {
    portfolioId = existingPortfolios[0].id
  }

  if (existingGoals.length === 0 && portfolioId) {
    // Same four starter targets the Goals page surfaces, but pre-scoped
    // to the seeded portfolio so the matching widgets light up with
    // GoalStatusChip and the goal-line overlay on first load.
    const seeds = [
      {
        name: 'Full & Open ≥ 60% by FY27',
        description: 'OSD-aligned target — 60% of FAR awards full and open by end of FY27.',
        measure: 'competition_health',
        target_value: { value: 60, direction: 'higher_is_better' as const, unit: 'percent' },
        effective_to: '2027-09-30',
      },
      {
        name: 'Small Business $ ≥ 25% by FY27',
        description: 'SBA Goaling target — 25% of obligated dollars to small businesses.',
        measure: 'small_biz_engagement',
        target_value: { value: 25, direction: 'higher_is_better' as const, unit: 'percent' },
        effective_to: '2027-09-30',
      },
      {
        name: 'Speed to Contract ≤ 90 days by FY27',
        description: 'Median solicitation-to-award day count under 90 days.',
        measure: 'speed_to_contract',
        target_value: { value: 90, direction: 'lower_is_better' as const, unit: 'days' },
        effective_to: '2027-09-30',
      },
      {
        name: 'OT Awards ≥ 15% of awards by FY27',
        description: 'Push commercial-tech / non-FAR pathways — 15% of awards via OT.',
        measure: 'ot_prevalence',
        target_value: { value: 15, direction: 'higher_is_better' as const, unit: 'percent' },
        effective_to: '2027-09-30',
      },
    ]
    for (const s of seeds) {
      try {
        await execAPI.createGoal({
          name: s.name,
          description: s.description,
          measure: s.measure,
          target_value: s.target_value,
          office: null,
          portfolio_group: portfolioId,
          benchmark_sources: [],
          effective_from: null,
          effective_to: s.effective_to,
        } as never)
      } catch (err) {
        console.warn('Demo goal seed failed:', s.name, err)
      }
    }
  }

  return portfolioId
}

export default function ExecDashboard() {
  const [dashboardId, setDashboardId] = useState<string | null>(null)
  const [initializing, setInitializing] = useState(true)

  // On mount, load or create the user's default dashboard. If the URL
  // carries `?dashboard={id}`, prefer that over the user's default so a
  // pasted share-URL lands on the same dashboard the sender saw (subject
  // to the recipient's read permissions on the backend).
  useEffect(() => {
    async function init() {
      try {
        // Seed demo state in dev (no-op in prod). Returns the portfolio_group
        // id we'll scope a fresh dashboard to — peer-line, Attention widget,
        // and GoalStatusChip all come alive once the dashboard is scoped.
        const seededPortfolioId = await seedDemoStateIfDev()

        const urlDashId = new URLSearchParams(window.location.search).get('dashboard')
        const dashboards = await execAPI.getDashboards()
        const fromUrl = urlDashId ? dashboards.find((d) => d.id === urlDashId) : undefined
        const defaultDash = dashboards.find((d) => d.is_default)
        const targetDash = fromUrl ?? defaultDash ?? (dashboards.length > 0 ? dashboards[0] : undefined)

        if (targetDash) {
          // Self-heal: if the saved layout for a default dashboard is
          // obviously stale (e.g., everything stacked at x=0 because the
          // dashboard was created before today's curated layout), re-pack
          // it once on load. This keeps the "preview just works" promise
          // for existing accounts, not just first-time leaders.
          if (targetDash.is_default) {
            const full = await execAPI.getDashboard(targetDash.id)
            const widgets = full.widgets || []
            if (isLayoutBroken(full.layout || [], widgets.length)) {
              const tiles = widgets.map((w) => ({ id: w.id, ...widgetDefaultSize(w) }))
              const repacked = packLayout(tiles)
              await execAPI.updateLayout(targetDash.id, repacked)
            }
            // Dev convenience: if the existing dashboard predates the
            // demo seed and has no portfolio scope, attach the seeded
            // portfolio. Idempotent — only patches when scope is null.
            if (seededPortfolioId && !full.portfolio_group) {
              await execAPI.updateDashboard(targetDash.id, { portfolio_group: seededPortfolioId } as never)
            }
          }
          setDashboardId(targetDash.id)
        } else {
          // Create a new default dashboard with starter widgets (G5).
          // In dev, scope it to the seeded portfolio so the peer-line
          // overlay + Attention widget render meaningfully on first load.
          const newDash = await execAPI.createDashboard({
            name: 'Executive Dashboard',
            is_default: true,
            layout: [],
            ...(seededPortfolioId ? { portfolio_group: seededPortfolioId } : {}),
          } as never)
          const tiles: { id: string; w: number; h: number }[] = []
          for (const def of DEFAULT_WIDGETS) {
            const widget = await execAPI.addWidget(newDash.id, {
              measure: def.measure,
              title: def.title,
              config: def.config as WidgetConfig | undefined,
            })
            tiles.push({ id: widget.id, w: def.w, h: def.h })
          }
          await execAPI.updateLayout(newDash.id, packLayout(tiles))
          setDashboardId(newDash.id)
        }
      } catch (err) {
        console.error('Failed to initialize exec dashboard:', err)
      } finally {
        setInitializing(false)
      }
    }
    init()
  }, [])

  if (initializing) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    )
  }

  return (
    <ExecDashboardProvider dashboardId={dashboardId}>
      <DashboardContent />
    </ExecDashboardProvider>
  )
}

// ---------------------------------------------------------------------------
// Inner content — has access to ExecDashboardContext
// ---------------------------------------------------------------------------

export function DashboardContent() {
  const {
    dashboard,
    loading,
    error,
    filters,
    setFilters,
    refetchDashboard,
    chartGroup,
  } = useExecDashboard()
  const { updatePageContext } = useAgent()

  // Phase F1: connect every chart in this dashboard to a shared group so
  // hovering a date in one widget shows the same date crosshair on every
  // other time-axis widget. Renderers set `chart.group = chartGroup` on
  // ready; this effect kicks `echarts.connect` once after widgets are
  // present (idempotent — late-mounted widgets join automatically).
  useEffect(() => {
    if (!chartGroup) return
    const t = setTimeout(() => connectChartGroup(chartGroup), 200)
    return () => clearTimeout(t)
  }, [chartGroup, dashboard?.widgets?.length])

  // Push dashboard ID into agent page context so exec tools know which dashboard to operate on
  useEffect(() => {
    if (dashboard?.id) {
      updatePageContext({ dashboardId: dashboard.id })
    }
  }, [dashboard?.id, updatePageContext])

  const [pickerOpen, setPickerOpen] = useState(false)
  const layoutSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Share-this-view URL state (Phase J5) ──────────────────────────
  // Encode the dashboard ID + FY range into search params so the URL
  // faithfully reproduces the current view when pasted. The dashboard ID
  // is what makes the share-URL useful between users — without it, every
  // recipient lands on their own default dashboard regardless of what the
  // sender was looking at. Permissions still apply server-side.
  const [searchParams, setSearchParams] = useSearchParams()
  const [shareCopied, setShareCopied] = useState(false)

  // Hydrate filters from URL on first mount.
  useEffect(() => {
    const fyParam = searchParams.get('fy')
    if (fyParam && fyParam !== filters.fiscal_year) {
      setFilters({ ...filters, fiscal_year: fyParam })
    }
    // Only run once on mount.
  }, [])

  // Reflect filter + dashboard-id changes back into URL params so the
  // current location IS the share URL — no separate "generate link" step.
  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    let changed = false
    const fy = filters.fiscal_year
    if (fy && fy !== searchParams.get('fy')) {
      next.set('fy', String(fy))
      changed = true
    }
    if (dashboard?.id && dashboard.id !== searchParams.get('dashboard')) {
      next.set('dashboard', dashboard.id)
      changed = true
    }
    if (changed) setSearchParams(next, { replace: true })
  }, [filters.fiscal_year, dashboard?.id])

  const handleShareView = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 1500)
    } catch (err) {
      console.error('Share copy failed:', err)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  // FY range for the slider

  // ── Layout change handler (debounced save) ──────────────────────

  const handleLayoutChange = useCallback(
    (newLayout: ReactGridLayoutNS.Layout[]) => {
      if (!dashboard) return

      const gridItems: GridLayoutItem[] = newLayout.map((l) => ({
        i: l.i,
        x: l.x,
        y: l.y,
        w: l.w,
        h: l.h,
      }))

      // Debounce: save after 500ms of no changes
      if (layoutSaveTimer.current) clearTimeout(layoutSaveTimer.current)
      layoutSaveTimer.current = setTimeout(() => {
        execAPI.updateLayout(dashboard.id, gridItems).catch(console.error)
      }, 500)
    },
    [dashboard],
  )

  // ── Widget CRUD ─────────────────────────────────────────────────

  const handleAddWidget = useCallback(
    async (measure: string, config?: WidgetConfig, title?: string, gridW?: number, gridH?: number) => {
      if (!dashboard) return
      try {
        const widget = await execAPI.addWidget(dashboard.id, {
          measure,
          ...(config ? { config } : {}),
          ...(title ? { title } : {}),
        })
        // Add to layout at the bottom
        const maxY = dashboard.layout.reduce((max, item) => Math.max(max, item.y + item.h), 0)
        const newLayout: GridLayoutItem[] = [
          ...dashboard.layout,
          { i: widget.id, x: 0, y: maxY, w: gridW ?? 6, h: gridH ?? 5 },
        ]
        await execAPI.updateLayout(dashboard.id, newLayout)
        await refetchDashboard()
      } catch (err) {
        console.error('Failed to add widget:', err)
      }
    },
    [dashboard, refetchDashboard],
  )

  const handleRemoveWidget = useCallback(
    async (widgetId: string) => {
      if (!dashboard) return
      try {
        await execAPI.removeWidget(dashboard.id, widgetId)
        // Remove from layout
        const newLayout = dashboard.layout.filter((item) => item.i !== widgetId)
        await execAPI.updateLayout(dashboard.id, newLayout)
        await refetchDashboard()
      } catch (err) {
        console.error('Failed to remove widget:', err)
      }
    },
    [dashboard, refetchDashboard],
  )

  const handleUpdateWidget = useCallback(
    async (widgetId: string, data: Partial<DashboardWidget>) => {
      if (!dashboard) return
      try {
        await execAPI.updateWidget(dashboard.id, widgetId, data)
        await refetchDashboard()
      } catch (err) {
        console.error('Failed to update widget:', err)
      }
    },
    [dashboard, refetchDashboard],
  )

  // ── Render ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 text-red-500">
        {error}
      </div>
    )
  }

  if (!dashboard) return null

  // Build layout from dashboard model, with sensible defaults for any widgets
  // that don't have a layout entry yet
  const layoutEntries = new Set(dashboard.layout.map((l) => l.i))
  const layout: GridLayoutItem[] = [...dashboard.layout]

  // Add layout entries for widgets without one
  let nextY = layout.reduce((max, item) => Math.max(max, item.y + item.h), 0)
  for (const widget of dashboard.widgets) {
    if (!layoutEntries.has(widget.id)) {
      layout.push({ i: widget.id, x: 0, y: nextY, w: 6, h: 5 })
      nextY += 5
    }
  }

  // Sort: pinned widgets first
  const sortedWidgets = [...dashboard.widgets].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1
    if (!a.pinned && b.pinned) return 1
    return 0
  })

  return (
    <div className="p-4 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <LayoutDashboard size={20} className="text-gray-500" />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {dashboard.name}
          </h1>
          <DataFreshnessPill />
        </div>

        <div className="flex items-center gap-3 flex-1 max-w-xl ml-4">
          {/* FY range slider */}
          <div className="flex-1">
            <FYRangeSlider
              value={filters.fiscal_year}
              onChange={(val) => setFilters({ ...filters, fiscal_year: val })}
            />
          </div>

          {/* Share-this-view URL (J5) */}
          <button
            onClick={handleShareView}
            title="Copy a link that reproduces this view"
            className="exec-no-print p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {shareCopied ? <Check size={14} className="text-green-500" /> : <Share2 size={14} />}
          </button>

          {/* Print one-pager (J6) */}
          <button
            onClick={handlePrint}
            title="Print one-pager"
            className="exec-no-print p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <Printer size={14} />
          </button>

          {/* Add Widget */}
          <button
            onClick={() => setPickerOpen(true)}
            className="exec-no-print flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            <Plus size={16} />
            Add Widget
          </button>
        </div>
      </div>

      {/* Goals Panel + Attention Widget (Phase B10/D5 + Phase I1).
          Side-by-side flex container — Goals on left, Attention on right.
          Horizontal margin matches react-grid-layout's default `margin={[10,10]}`
          so the row aligns with widgets below. */}
      <div className="mb-4 mx-2.5 grid grid-cols-1 lg:grid-cols-2 gap-3">
        <GoalsPanel officeId={dashboard.office} />
        <AttentionWidget />
      </div>

      {/* Widget Grid */}
      {dashboard.widgets.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-96 text-gray-400">
          <LayoutDashboard size={48} className="mb-3 opacity-40" />
          <p className="text-sm">No widgets yet. Click "Add Widget" to get started.</p>
        </div>
      ) : (
        <ResponsiveGridLayout
          className="layout"
          layouts={{ lg: layout }}
          breakpoints={{ lg: 1200, md: 996, sm: 768 }}
          cols={{ lg: 12, md: 12, sm: 6 }}
          rowHeight={60}
          isDraggable
          isResizable
          onLayoutChange={handleLayoutChange}
          draggableHandle=".widget-drag-handle"
        >
          {sortedWidgets.map((widget) => (
            <div key={widget.id}>
              <WidgetCard
                widget={widget}
                onRemove={handleRemoveWidget}
                onUpdate={handleUpdateWidget}
              >
                <WidgetErrorBoundary widgetName={widget.title || widget.measure}>
                  <MeasureWidget widget={widget} />
                </WidgetErrorBoundary>
              </WidgetCard>
            </div>
          ))}
        </ResponsiveGridLayout>
      )}

      {/* Widget Picker Modal */}
      <WidgetPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAdd={handleAddWidget}
      />
    </div>
  )
}
